const SOLO_PLAN_CODE = 'BASIC';
export const TEAM_1499_MEMBER_LIMIT = 10;
export const TEAM_2999_MEMBER_LIMIT = 30;
export const TEAM_PRO_MEMBER_LIMIT = 50;
export const TEAM_9999_MEMBER_LIMIT = 100;

const TEAM_MEMBER_LIMIT_BY_PLAN_CODE: Record<string, InviteMemberLimit> = {
    TEAM_1499: TEAM_1499_MEMBER_LIMIT,
    TEAM_2999: TEAM_2999_MEMBER_LIMIT,
    PRO: TEAM_PRO_MEMBER_LIMIT,
    TEAM_9999: TEAM_9999_MEMBER_LIMIT,
    TEAM_49999: null,
};

const TEAM_PLANS_WITH_HIGHER_SELF_SERVICE_MEMBER_TIER = new Set([
    'TEAM_1499',
    'TEAM_2999',
    'PRO',
    'TEAM_9999',
]);

type InviteMemberLimit = number | null;

export interface WorkspaceMemberLimitEntitlement {
    configured?: boolean | null;
    unlimited?: boolean | null;
    limitValue?: number | string | null;
    source?: string | null;
}

const normalizePlanCode = (planCode?: string | null): string => {
    return (planCode || '').trim().toUpperCase();
};

const normalizePositiveInteger = (value?: number | string | null): number | null => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    const parsedValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
        return null;
    }

    return Math.floor(parsedValue);
};

export const resolveInvitePlanCode = ({
    currentWorkspacePlanCode,
    billingSummaryPlanCode,
    userPlanCode,
}: {
    currentWorkspacePlanCode?: string | null;
    billingSummaryPlanCode?: string | null;
    userPlanCode?: string | null;
}): string => {
    return normalizePlanCode(currentWorkspacePlanCode)
        || normalizePlanCode(billingSummaryPlanCode)
        || normalizePlanCode(userPlanCode);
};

export const isSoloInviteUpgradeRequired = (planCode?: string | null): boolean => {
    const normalizedPlanCode = normalizePlanCode(planCode);
    return normalizedPlanCode === SOLO_PLAN_CODE || normalizedPlanCode.startsWith('SOLO_');
};

export const resolveInviteMemberLimit = (planCode?: string | null): InviteMemberLimit => {
    const normalizedPlanCode = normalizePlanCode(planCode);
    if (Object.prototype.hasOwnProperty.call(TEAM_MEMBER_LIMIT_BY_PLAN_CODE, normalizedPlanCode)) {
        return TEAM_MEMBER_LIMIT_BY_PLAN_CODE[normalizedPlanCode];
    }
    return null;
};

export const resolveInvitePlanName = (planCode?: string | null): 'Solo' | 'Team' | null => {
    const normalizedPlanCode = normalizePlanCode(planCode);
    if (normalizedPlanCode === SOLO_PLAN_CODE || normalizedPlanCode.startsWith('SOLO_')) {
        return 'Solo';
    }
    if (Object.prototype.hasOwnProperty.call(TEAM_MEMBER_LIMIT_BY_PLAN_CODE, normalizedPlanCode)) {
        return 'Team';
    }
    return null;
};

export const hasHigherSelfServiceMemberTier = (planCode?: string | null): boolean => (
    TEAM_PLANS_WITH_HIGHER_SELF_SERVICE_MEMBER_TIER.has(normalizePlanCode(planCode))
);

export const resolveEffectiveInviteMemberLimit = ({
    planCode,
    memberLimit,
}: {
    planCode?: string | null;
    memberLimit?: WorkspaceMemberLimitEntitlement | null;
}): InviteMemberLimit => {
    if (memberLimit?.unlimited) {
        return null;
    }

    const entitlementLimit = normalizePositiveInteger(memberLimit?.limitValue);
    if (entitlementLimit !== null) {
        return entitlementLimit;
    }

    return resolveInviteMemberLimit(planCode);
};

export const isMemberLimitReached = ({
    memberCount,
    memberLimit,
}: {
    memberCount: number;
    pendingInviteCount?: number;
    memberLimit?: InviteMemberLimit;
}): boolean => {
    if (!memberLimit) {
        return false;
    }

    return memberCount >= memberLimit;
};

export const isTeamMemberLimitReached = ({
    planCode,
    memberCount,
}: {
    planCode?: string | null;
    memberCount: number;
    pendingInviteCount?: number;
}): boolean => {
    const memberLimit = resolveInviteMemberLimit(planCode);

    return isMemberLimitReached({
        memberCount,
        memberLimit,
    });
};
